import { useEffect, useRef, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import { QueryDocumentSnapshot } from "firebase/firestore";
import { getReportFolderPath } from "shared/utils/reportFolderUtils";
import { ContentSettings } from "shared/types/Homework";
import { mapWithConcurrency } from "shared/utils/mapWithConcurrency";
import { createFirestoreReadCache } from "../homeworkReadCache";
import { HOMEWORK_CALENDAR_CONCURRENCY, scheduleHomeworkAssignments } from "../homeworkCalendarSchedule";

type FirestoreReaders = Pick<ReturnType<typeof useFirestore>, "readFirestoreOneDoc" | "readFirestore">;

interface report{
    release_id:string,
    title:string,
    date:Date,
    id:string,
    path:string,
    folderPath: string[],
    order: number,
    report_setting?: ContentSettings | null
}

interface AssignedData {
    release_id: string;
    distribution_id?: string;
    dayOfWeek: boolean[];
    interval: number;
    date: string;
    effective_date: {
        start: string;
        end: string;
    };
    report: {
        path: string;
        id: string;
        order: number;
    }
    report_setting?: ContentSettings | null;
}

const useHomeworkReportView = (selected_lesson:string, selected_month:Date) => {
    const {readFirestoreOneDoc, readFirestore, deleteFirestore} = useFirestore();
    const [reportList, setReportList] = useState<report[]>([])
    const [isOpenLoadingModal, setIsOpenLoadingModal] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [retryVersion, setRetryVersion] = useState(0);
    const requestIdRef = useRef(0);
    const selectedMonthKey = `${selected_month.getFullYear()}-${selected_month.getMonth()}`;

    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setReportList([]);
        setLoadError(null);
        if(selected_lesson){
            getReportList(requestId);
        } else {
            setIsOpenLoadingModal(false);
            setLoadingProgress(0);
        }
        return () => { requestIdRef.current = requestId + 1; };
    }, [selected_lesson, selectedMonthKey, retryVersion])

    const getFolderPath = (path: string, readOneDoc: FirestoreReaders["readFirestoreOneDoc"]) => getReportFolderPath(readOneDoc, path).catch(error => {
        console.error('Error fetching report folder:', error);
        return [] as string[];
    });

    const getReportList = async(requestId: number) => {
        setIsOpenLoadingModal(true);
        setLoadingProgress(0);
        try {
            const assigned_list = await getAssignedReportData();
            if (requestId !== requestIdRef.current) return;
            // 전체 배포 순서로 반복 날짜를 계산한 뒤 현재 월의 과제만 상세 조회한다.
            const scheduledReports = scheduleHomeworkAssignments(assigned_list, selected_month);
            const readers = createFirestoreReadCache({ readFirestoreOneDoc, readFirestore });

            const totalReports = scheduledReports.length;
            let processedReports = 0;

            const reports = await mapWithConcurrency(scheduledReports, async (assigned_data) => {
                if (requestId !== requestIdRef.current) return null;
                const [report_data, folderPath] = await Promise.all([
                    getReport({id: assigned_data.report.id, path: assigned_data.report.path}, readers),
                    getFolderPath(assigned_data.report.path, readers.readFirestoreOneDoc)
                ]);
                processedReports++;
                if (requestId === requestIdRef.current) {
                    setLoadingProgress((processedReports / totalReports) * 100);
                }
                
                return {
                    release_id: assigned_data.release_id,
                    title: report_data?.name || '',
                    date: assigned_data.scheduledDate,
                    id: assigned_data.report.id,
                    path: assigned_data.report.path,
                    folderPath,
                    order: assigned_data.report.order,
                    report_setting: assigned_data.report_setting
                }
            }, HOMEWORK_CALENDAR_CONCURRENCY);

            if (requestId !== requestIdRef.current) return;
            const validReports = reports.filter((report): report is NonNullable<typeof report> => report !== null);
            setReportList(validReports);
        } catch (error) {
            console.error('Error fetching report list:', error);
            if (requestId === requestIdRef.current) {
                setReportList([]);
                setLoadError('과제 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsOpenLoadingModal(false);
                setLoadingProgress(0);
            }
        }
    }

    const getReport = async(assigned:{id:string, path:string}, readers: FirestoreReaders) => {
        try {
            const [reportDoc, contentQuery] = await Promise.all([
                readers.readFirestoreOneDoc(`${assigned.path}`, assigned.id),
                readers.readFirestore(`${assigned.path}/${assigned.id}/Content`)
            ]);

            if (!reportDoc.exists()) {
                return null;
            }

            const report = {
                id: reportDoc.id,
                name: reportDoc.data().name,
                path: assigned.path
            };

            return contentQuery.empty ? null : report;
        } catch (error) {
            console.error('Error fetching report:', error);
            return null;
        }
    }

    const getAssignedReportData = async(): Promise<AssignedData[]> => {
        // 오래전에 시작한 반복 배포도 전체 순서가 있어야 현재 월의 날짜를 계산할 수 있다.
        const response = await readFirestore(`ReportAssignLesson/${selected_lesson}/Report`);
        // 기존 Firestore 훅은 실패 시 Error를 반환하므로 빈 목록과 구분해야 한다.
        if (!response || !Array.isArray(response.docs)) {
            throw response instanceof Error ? response : new Error('Invalid assignment response');
        }
    
        if (response.empty) {
            return [];
        }

        return response.docs.map((doc: QueryDocumentSnapshot) => {
            const assigned_data = doc.data();
            return {
                release_id: doc.id,
                distribution_id: assigned_data.distribution_id,
                effective_date: assigned_data.effective_date ?? { start: "", end: "" },
                dayOfWeek: Array.isArray(assigned_data.dayOfWeek) ? assigned_data.dayOfWeek : [],
                order: assigned_data.report?.order ?? assigned_data.order ?? 0,
                interval: assigned_data.interval,
                date: assigned_data.date ?? "",
                report: {
                    path: assigned_data.report?.path ?? "",
                    id: assigned_data.report?.id ?? "",
                    order: assigned_data.report?.order ?? assigned_data.order ?? 0,
                },
                report_setting: assigned_data.report_setting
            };
        });
    }
    
    const deleteReport = async(id:string) => {
        try {
            await deleteFirestore(`ReportAssignLesson/${selected_lesson}/Report`, id);
            setReportList(prevList => prevList.filter(report => report.release_id !== id));
        } catch (error) {
            console.error('Error deleting report:', error);
        }
    }

    return {
        reportList, 
        isOpenLoadingModal, 
        loadingProgress,
        loadError,
        retryLoad: () => setRetryVersion(version => version + 1),
        deleteReport
    };
}

export default useHomeworkReportView;
